import React, { Fragment, useEffect, useState } from "react";
import styled from "styled-components";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Flex } from "../../../../../components/Flex";
import { Text } from "../../../../../components/Text";

import { CreateFormDto } from "../../../../../model/form/dto/CreateFormDto";
import { UpdateFormDto } from "../../../../../model/form/dto/UpdateFormDto";
import { Spacing } from "../../../../../theme/Spacing";
import { TFunction } from "i18next";
import { Switch, Tooltip } from "../../../../../utils/local-antd";
import { AtiraIcon } from "../../../../../components/AtiraIcon";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons/faInfoCircle";
import { Button } from "../../../../../components/Button";
import { AtiraSelect } from "../../../../../components/AtiraSelect";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { TimezonePicker } from "../../../../../components/TimezonePicker";
import { TestIDs } from "../../../../../tests/types/testIds";
import { mergeHourRanges, splitHourRange } from "../../../../../utils/Date";
import { Info } from "../../../../../components/Info";
import { isBlankValue } from "../../../../../utils/isBlankValue";
import { useAppSelector } from "../../../../../redux/store";
import { userSliceSelectors } from "../../../../../redux/user/user.selector";

const getDays = (t: TFunction) => [
  { label: t("common.days.monday"), value: "monday" },
  { label: t("common.days.tuesday"), value: "tuesday" },
  { label: t("common.days.wednesday"), value: "wednesday" },
  { label: t("common.days.thursday"), value: "thursday" },
  { label: t("common.days.friday"), value: "friday" },
  { label: t("common.days.saturday"), value: "saturday" },
  { label: t("common.days.sunday"), value: "sunday" },
];

const getHours = (from: number | string = 0) => {
  if (typeof from === "string") {
    const hour = parseInt(from.split(":")[0], 10);
    const minute = parseInt(from.split(":")[1], 10);

    from = hour + (minute ? 1 : 0.5);
  }

  const hours = [];
  for (let i = from; i < 24; i += 0.5) {
    const isHour = i % 1 === 0;

    const hourString = isHour
      ? i.toString().padStart(2, "0") + ":00"
      : (i - 0.5).toString().padStart(2, "0") + ":30";
    hours.push({ label: hourString, value: hourString });
  }
  return hours;
};

const withOptionTestIds = (
  hours: Array<{ label: string; value: string }>,
  testIdFor: (value: string) => string,
) =>
  hours.map((hour) => ({
    ...hour,
    "data-testid": testIdFor(hour.value),
  }));

const SLOT_DURATION_OPTIONS_MINUTES = [5, 10, 15, 20, 30, 45, 60, 90, 120];
const DEFAULT_SLOT_DURATION_MINUTES = 30;

const Required = styled.span`
  color: ${({ theme }) => theme.danger};
`;

const Container = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.l};
`;

const SectionDivider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.sub};
  margin: 0;
  width: 100%;
`;

const DaysRow = styled(Flex)`
  flex-wrap: wrap;
  gap: ${Spacing.xs};
`;

const DayChip = styled.button<{ $active: boolean }>`
  padding: ${() => `${Spacing.xs} ${Spacing.m}`};
  border-radius: 999px;
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.main : "rgba(0,0,0,0.1)")};
  background-color: ${({ theme, $active }) =>
    $active ? theme.main : "transparent"};
  color: ${({ $active, theme }) => ($active ? theme.white : theme.main)};
  cursor: pointer;
`;

const HourRow = styled(Flex)`
  align-items: center;
  justify-content: space-between;
`;

const DeleteButton = styled(Button)`
  background-color: ${({ theme }) => theme.danger};
  width: 6rem;
  margin: 0;

  &:hover {
    filter: brightness(0.9);
    -webkit-filter: brightness(0.9);
  }
`;

interface InputConfigDateProps {
  inputIndex: number;
}

export const InputConfigDate: React.FC<InputConfigDateProps> = ({
  inputIndex,
}) => {
  const { t } = useTranslation();
  const { control, watch, setValue } =
    useFormContext<Partial<CreateFormDto | UpdateFormDto>>();

  const [timeRangStart, setTimeRangeStart] = useState("");
  const [timeRangEnd, setTimeRangeEnd] = useState("");

  const basePath = `inputs.${inputIndex}.dateInputConfig` as const;

  const inputName = watch(`inputs.${inputIndex}.name`) || String(inputIndex);
  const selectedDays = watch(`${basePath}.days`) || [];
  const selectedHours = watch(`${basePath}.hours`) || [];
  const selectedTimezone = watch(`${basePath}.timezone`);
  const selectedEnabled = watch(`${basePath}.enabled`);
  const selectedSlotDuration =
    watch(`${basePath}.slotDurationMinutes`) || DEFAULT_SLOT_DURATION_MINUTES;
  const hasOverlappingHours =
    selectedHours.length > 1 &&
    mergeHourRanges(selectedHours).length < selectedHours.length;
  // The switch's own value is authoritative once the admin has actually
  // touched it. Until then, it reflects whatever a form saved before this
  // switch existed already implies - so opening an existing bookable field
  // shows it correctly ON (with its existing config intact) instead of
  // defaulting OFF and silently disabling it on the next save. Mirrors
  // UtilsService.isCustomDateInput / utils/isCustomDateInput on the API and
  // the rest of the app.
  const isBookingEnabled =
    selectedEnabled != null
      ? Boolean(selectedEnabled)
      : Boolean(
          !isBlankValue(selectedDays) ||
            !isBlankValue(selectedHours) ||
            !isBlankValue(selectedTimezone),
        );

  const accountTimezone = useAppSelector(userSliceSelectors.selectLoggedInUser)
    ?.settings?.timezone;

  useEffect(() => {
    if (isBookingEnabled && !selectedTimezone && accountTimezone) {
      setValue(`${basePath}.timezone`, accountTimezone);
    }
  }, [isBookingEnabled, selectedTimezone, accountTimezone, basePath, setValue]);

  const toggleDay = (day: string) => {
    const next = selectedDays.includes(day)
      ? selectedDays.filter((d) => d !== day)
      : [...selectedDays, day];

    setValue(`${basePath}.days`, next, { shouldDirty: true });
  };

  const addTimeRange = () => {
    if (!timeRangStart || !timeRangEnd) {
      return;
    }

    const startHour = parseInt(timeRangStart.split(":")[0], 10);
    const endHour = parseInt(timeRangEnd.split(":")[0], 10);

    if (endHour < startHour) {
      return;
    }

    setValue(
      `${basePath}.hours`,
      [...selectedHours, { from: timeRangStart, to: timeRangEnd }],
      { shouldDirty: true },
    );

    setTimeRangeStart("");
    setTimeRangeEnd("");
  };

  const removeHour = (index: number) => {
    const next = [...selectedHours];
    next.splice(index, 1);
    setValue(`${basePath}.hours`, next, { shouldDirty: true });
  };

  return (
    <Container>
      <Flex alignItems="center" justifyContent="space-between">
        <Flex alignItems="center" gap="xs">
          <Tooltip title={t("input.dateConfig.enabled.info")}>
            <span>
              <AtiraIcon icon={faInfoCircle} color="main" size="sm" />
            </span>
          </Tooltip>
          <Text>{t("input.inputConfig.enabled.title")}</Text>
        </Flex>
        <Switch
          checked={isBookingEnabled}
          onChange={(checked) =>
            setValue(`${basePath}.enabled`, checked, { shouldDirty: true })
          }
          className={TestIDs.forms.createEdit.steps.step2.inputConfigureModal.dateConfig.enabled(
            inputName,
          )}
        />
      </Flex>

      {isBookingEnabled ? (
        <Fragment>
          <Flex flexDirection="column" gap="xs">
            <Flex alignItems="center" gap="xs">
              <Tooltip title={t("input.dateConfig.days.info")}>
                <span>
                  <AtiraIcon icon={faInfoCircle} color="main" size="sm" />
                </span>
              </Tooltip>

              <Text>
                {t("input.inputConfig.days.title")}
                <Required> *</Required>
              </Text>
            </Flex>
            <DaysRow>
              {getDays(t).map((day) => (
                <DayChip
                  key={day.value}
                  $active={selectedDays.includes(day.value)}
                  onClick={() => toggleDay(day.value)}
                  data-testid={TestIDs.forms.createEdit.steps.step2.inputConfigureModal.dateConfig.day(
                    inputName,
                    day.value,
                  )}
                >
                  {day.label}
                </DayChip>
              ))}
            </DaysRow>
          </Flex>

          <Controller
            control={control}
            name={`${basePath}.timezone`}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TimezonePicker
                value={value}
                onChange={(e: any) => onChange(e?.value)}
                error={error}
                info={t("input.dateConfig.timezone.info")}
                required
                className={TestIDs.forms.createEdit.steps.step2.inputConfigureModal.dateConfig.timezone(
                  inputName,
                )}
              />
            )}
          />

          <SectionDivider />

          <Flex flexDirection="column" gap="xs">
            <Flex alignItems="center" gap="xs">
              <Tooltip title={t("input.dateConfig.slotDuration.info")}>
                <span>
                  <AtiraIcon icon={faInfoCircle} color="main" size="sm" />
                </span>
              </Tooltip>
              <Text>{t("input.inputConfig.slotDuration.title")}</Text>
            </Flex>
            <Controller
              control={control}
              name={`${basePath}.slotDurationMinutes`}
              render={({ field: { value, onChange } }) => (
                <AtiraSelect
                  style={{ width: "8rem" }}
                  className={TestIDs.forms.createEdit.steps.step2.inputConfigureModal.dateConfig.slotDuration(
                    inputName,
                  )}
                  options={withOptionTestIds(
                    SLOT_DURATION_OPTIONS_MINUTES.map((minutes) => ({
                      label: t("input.dateConfig.slotDuration.optionLabel", {
                        minutes,
                      }),
                      value: String(minutes),
                    })),
                    (minutes) =>
                      TestIDs.forms.createEdit.steps.step2.inputConfigureModal.dateConfig.slotDurationOption(
                        inputName,
                        minutes,
                      ),
                  )}
                  value={String(value || DEFAULT_SLOT_DURATION_MINUTES)}
                  onChange={(v) => onChange(Number(v))}
                  virtual={false}
                />
              )}
            />
          </Flex>

          <Flex flexDirection="column" gap="xs">
            <Flex alignItems="center" gap="xs">
              <Tooltip title={t("input.dateConfig.hours.info")}>
                <span>
                  <AtiraIcon icon={faInfoCircle} color="main" />
                </span>
              </Tooltip>
              <Text>{t("input.inputConfig.hours.title")}</Text>
              <Required> *</Required>
            </Flex>

            <Flex flexDirection="column" gap="m">
              <Flex gap="s" justifyContent="space-between">
                <Flex alignItems="center" gap="s">
                  <AtiraSelect
                    placeholder={t("common.start")}
                    style={{ width: "8rem" }}
                    className={TestIDs.forms.createEdit.steps.step2.inputConfigureModal.dateConfig.hourStart(
                      inputName,
                    )}
                    options={withOptionTestIds(
                      getHours().filter(
                        (h) => !selectedHours.some((sh) => sh.from === h.value),
                      ),
                      (hour) =>
                        TestIDs.forms.createEdit.steps.step2.inputConfigureModal.dateConfig.hourStartOption(
                          inputName,
                          hour,
                        ),
                    )}
                    value={timeRangStart || null}
                    onChange={(v) => setTimeRangeStart(v)}
                    required
                    virtual={false}
                  />
                  <AtiraSelect
                    placeholder={t("common.end")}
                    style={{ width: "8rem" }}
                    className={TestIDs.forms.createEdit.steps.step2.inputConfigureModal.dateConfig.hourEnd(
                      inputName,
                    )}
                    options={withOptionTestIds(
                      getHours(timeRangStart),
                      (hour) =>
                        TestIDs.forms.createEdit.steps.step2.inputConfigureModal.dateConfig.hourEndOption(
                          inputName,
                          hour,
                        ),
                    )}
                    value={timeRangEnd || null}
                    onChange={(v) => setTimeRangeEnd(v)}
                    disabled={!timeRangStart}
                    required
                    virtual={false}
                  />
                </Flex>
                <Button
                  margin="none"
                  hover
                  disabled={
                    !timeRangStart ||
                    !timeRangEnd ||
                    timeRangEnd < timeRangStart
                  }
                  onClick={addTimeRange}
                  icon={faPlus}
                  width="6rem"
                  dataTestId={TestIDs.forms.createEdit.steps.step2.inputConfigureModal.dateConfig.addHour(
                    inputName,
                  )}
                >
                  {t("common.add")}
                </Button>
              </Flex>

              <Controller
                control={control}
                name={`${basePath}.hours`}
                render={({ fieldState: { error } }) => (
                  <Fragment>
                    {hasOverlappingHours ? (
                      <Info
                        color="warning"
                        info={t("input.dateConfig.hours.overlapNotice")}
                      />
                    ) : null}
                    <Flex flexDirection="column" gap="m">
                      {selectedHours.map((h, index) => {
                        const slots = splitHourRange(h, selectedSlotDuration);
                        const lastSlotEnd = slots[slots.length - 1]?.to;

                        return (
                          <Flex
                            key={`${h.from}-${h.to}-${index}`}
                            flexDirection="column"
                            gap="xs"
                          >
                            <HourRow>
                              <Text>{h.from}</Text>
                              <Text>{h.to}</Text>
                              <DeleteButton
                                icon={faTrash}
                                onClick={() => removeHour(index)}
                              >
                                {t("common.remove", "Remove")}
                              </DeleteButton>
                            </HourRow>
                            <Text fontSize="xs" color="darkerSub">
                              {slots.length
                                ? t("input.dateConfig.hours.preview", {
                                    count: slots.length,
                                    duration: selectedSlotDuration,
                                    end: lastSlotEnd,
                                  })
                                : t("input.dateConfig.hours.preview.noFit", {
                                    duration: selectedSlotDuration,
                                  })}
                              {slots.length && lastSlotEnd !== h.to
                                ? ` · ${t(
                                    "input.dateConfig.hours.preview.leftover",
                                    { from: lastSlotEnd, to: h.to },
                                  )}`
                                : ""}
                            </Text>
                          </Flex>
                        );
                      })}
                    </Flex>

                    {error?.message ? (
                      <Text color="danger" fontSize="s">
                        {error.message}
                      </Text>
                    ) : null}
                  </Fragment>
                )}
              />
            </Flex>
          </Flex>
        </Fragment>
      ) : null}
    </Container>
  );
};
